import React, { useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Paper,
} from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { login, clearError } from "./authSlice";

const validationSchema = Yup.object({
  email: Yup.string().trim().required("Required"),
  password: Yup.string().min(4, "Min 4 characters").required("Required"),
});

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error } = useSelector((s) => s.auth);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(login(values));
      if (login.fulfilled.match(result)) {
        toast.success("Welcome");
        navigate("/");
      }
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 380,
        p: { xs: 2.5, sm: 3.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "6px",
        backgroundColor: "background.paper",
        boxShadow: "0 1px 3px rgba(13,46,117,0.06), 0 4px 16px rgba(13,46,117,0.06)",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 2.5 }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Surya Solars"
          sx={{
            height: { xs: 96, sm: 112 },
            objectFit: "contain",
            display: "block",
            mx: "auto",
            filter: "drop-shadow(0 6px 14px rgba(13,46,117,0.12))",
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.25, fontSize: "0.75rem" }}>
          {error}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Username
            </Typography>
            <TextField
              fullWidth
              name="email"
              placeholder="your username"
              autoComplete="username"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              name="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={status === "loading"}
            startIcon={
              status === "loading" ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{ minHeight: 36, mt: 0.5 }}
          >
            {status === "loading" ? "Signing in…" : "Sign In"}
          </Button>
        </Stack>
      </form>

      <MuiLink
        href="tel:+919944171881"
        underline="none"
        sx={{
          mt: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          py: 1,
          borderRadius: 2,
          color: "primary.main",
          fontWeight: 600,
          fontSize: "0.8125rem",
          backgroundColor: "rgba(13,46,117,0.04)",
          transition: "background-color 0.15s",
          "&:hover": { backgroundColor: "rgba(13,46,117,0.08)" },
        }}
      >
        <PhoneOutlinedIcon fontSize="small" />
        Contact support · 9944171881
      </MuiLink>
    </Paper>
  );
}
